import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { entriesApi, filesApi } from '../utils/api'
import { normalizeTime, parseTimeRange, combineTimeRange } from '../utils/timeFormat'
import { t } from '../utils/translations'
import { openFileDownload, resolveFileRef } from '../utils/fileUrl'
import { formatDateDisplay, getUpcomingSundayOptions } from '../utils/sundayDisplay'

const CHURCHES = ['KTC', 'SSC', 'WIC', 'STC']
const ENTRY_TYPES = ['Sermon', 'SundaySchool', 'Event']
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB
const MAX_POSTER_SIZE = 10 * 1024 * 1024 // 10MB
const TITLE_MAX_CHARS = 15
const SUNDAY_SCHOOL_PAUSED_TITLE = '暫停一次'
const ALLOWED_FILE_TYPES = ['pdf', 'ppt', 'pptx', 'doc', 'docx', 'txt', 'md']
const ALLOWED_POSTER_TYPES = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff']
const PREFILL_ENTRY_TYPES = new Set(['Sermon', 'SundaySchool', 'Event'])
const DEFAULT_TIME_BY_TYPE = {
  Sermon: '10:45',
  SundaySchool: '09:30'
}
const POST_UNTIL_DEFAULT_DAYS = 100

/** Count Unicode characters (Chinese characters count as 1 each). */
function charCount(str) {
  return Array.from(str || '').length
}

function truncateChars(str, max) {
  return Array.from(str || '').slice(0, max).join('')
}

function addDaysYYYYMMDD(dateStr, days) {
  if (!dateStr || !/^\d{8}$/.test(dateStr)) return ''
  const year = parseInt(dateStr.substring(0, 4), 10)
  const month = parseInt(dateStr.substring(4, 6), 10) - 1
  const day = parseInt(dateStr.substring(6, 8), 10)
  const date = new Date(year, month, day)
  date.setDate(date.getDate() + days)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}${m}${d}`
}

function emptyForm(church = '') {
  return {
    church,
    type: 'Sermon',
    title: '',
    event_date: '',
    time: '',
    time_start: '',
    time_end: '',
    speaker: '',
    scripture: '',
    description: '',
    files: [],
    status: 'saved',
    post_to_main: true,
    poster_key: '',
    post_until: '',
    video_url: ''
  }
}

/** Build https://files.hkmbc.org/e/{date}{church}活動[title]speaker[HHMM-HHMM].mp4 */
function buildEventVideoSuggestion({ event_date, church, title, speaker, time_start, time_end }) {
  let name = ''
  if (event_date && /^\d{8}$/.test(event_date)) name += event_date
  if (church) name += church
  name += '活動'
  if (title?.trim()) name += `[${title.trim()}]`
  if (speaker?.trim()) name += speaker.trim()
  const start = normalizeTime(time_start)?.replace(':', '')
  const end = normalizeTime(time_end)?.replace(':', '')
  if (start && end) name += `[${start}-${end}]`
  else if (start) name += `[${start}]`
  if (!name || name === '活動') return ''
  return `https://files.hkmbc.org/e/${name}.mp4`
}

function getHongKongTodayStr() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Hong_Kong' }).replace(/-/g, '')
}

/** Visual: event_date before today (HK) */
function isPastEventDate(dateStr) {
  if (!dateStr || dateStr.length !== 8) return false
  return dateStr < getHongKongTodayStr()
}

/** Edit lock: event_date more than 3 years before today (HK) */
const PAST_EVENT_LOCK_DAYS = 365 * 3

function isPastEventLocked(dateStr) {
  if (!dateStr || dateStr.length !== 8) return false
  const hkDateStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Hong_Kong' })
  const [year, month, day] = hkDateStr.split('-').map(Number)
  const cutoff = new Date(year, month - 1, day)
  cutoff.setDate(cutoff.getDate() - PAST_EVENT_LOCK_DAYS)
  const y = cutoff.getFullYear()
  const m = String(cutoff.getMonth() + 1).padStart(2, '0')
  const d = String(cutoff.getDate()).padStart(2, '0')
  return dateStr <= `${y}${m}${d}`
}

export default function UserPage({ lang = 'zh' }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const returnToAdminRef = useRef(false)
  const returnPathRef = useRef('/admin')
  const prefillAppliedRef = useRef(false)
  const [entries, setEntries] = useState([])
  const [allEntries, setAllEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingEntry, setEditingEntry] = useState(null)
  const [lockWarning, setLockWarning] = useState(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showAllChurches, setShowAllChurches] = useState(false)
  const [showOnlyEvents, setShowOnlyEvents] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState(false)
  const [uploadingPoster, setUploadingPoster] = useState(false)
  const [posterPreviewUrl, setPosterPreviewUrl] = useState('')
  const [openAttachments, setOpenAttachments] = useState(null)

  // User's assigned churches
  const userChurches = useMemo(() => {
    return user?.churches || (user?.church ? [user.church] : [])
  }, [user])

  const sundayOptions = useMemo(() => getUpcomingSundayOptions(), [])

  // Form state
  const [formData, setFormData] = useState(() => emptyForm(''))
  const [sundaySchoolPaused, setSundaySchoolPaused] = useState(false)
  const postUntilBeforeStopRef = useRef('')

  const leaveFormAndMaybeReturn = () => {
    const shouldReturn = returnToAdminRef.current
    const returnPath = returnPathRef.current || '/admin'
    returnToAdminRef.current = false
    setFormData(emptyForm(userChurches[0] || ''))
    setSundaySchoolPaused(false)
    postUntilBeforeStopRef.current = ''
    setEditingEntry(null)
    setLockWarning(null)
    setShowForm(false)
    setPosterPreviewUrl('')
    if (shouldReturn) {
      navigate(returnPath)
    }
  }

  // Prefill form when opened from 講道主日學總覽 / 活動總覽
  useEffect(() => {
    if (prefillAppliedRef.current || !user || user.role === 'viewer') return

    const church = searchParams.get('church')
    const type = searchParams.get('type')
    const date = searchParams.get('date')
    const time = searchParams.get('time')
    const from = searchParams.get('from')

    if (!church || !type || !date) return
    if (from !== 'admin' && from !== 'events-overview') return
    if (!CHURCHES.includes(church) || !PREFILL_ENTRY_TYPES.has(type) || !/^\d{8}$/.test(date)) return

    prefillAppliedRef.current = true
    returnToAdminRef.current = true
    returnPathRef.current = from === 'events-overview' ? '/admin/events' : '/admin'

    const allowedChurches = user.churches || (user.church ? [user.church] : [])
    const canCreate =
      user.role === 'admin' ||
      user.role === 'sysadmin' ||
      allowedChurches.includes(church)

    if (!canCreate) {
      setError(lang === 'zh' ? '您沒有權限為此堂會新增條目' : 'You cannot create entries for this church')
      setSearchParams({}, { replace: true })
      return
    }

    setFormData({
      ...emptyForm(church),
      type,
      event_date: date,
      time: time || DEFAULT_TIME_BY_TYPE[type] || '',
      ...(type === 'Event'
        ? (() => {
            const range = parseTimeRange(time || '')
            return {
              time_start: range.start,
              time_end: range.end,
              post_until: addDaysYYYYMMDD(date, POST_UNTIL_DEFAULT_DAYS)
            }
          })()
        : {})
    })
    setEditingEntry(null)
    setShowForm(true)
    setSearchParams({}, { replace: true })
  }, [user, searchParams, setSearchParams, lang])

  // Load entries on mount
  useEffect(() => {
    loadEntries()
  }, [])

  useEffect(() => {
    if (!openAttachments) return undefined
    const close = () => setOpenAttachments(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [openAttachments])

  const loadEntries = async () => {
    try {
      const response = await entriesApi.list()
      const data = response.data.data || []
      setAllEntries(data)
    } catch (err) {
      console.error('Failed to load entries:', err)
    } finally {
      setLoading(false)
    }
  }

  // Filter entries based on toggles
  useEffect(() => {
    let filtered = allEntries
    if (!showAllChurches) {
      filtered = filtered.filter(e => userChurches.includes(e.church))
    }
    if (showOnlyEvents) {
      filtered = filtered.filter(e => e.type === 'Event')
    } else {
      filtered = filtered.filter(e => e.type !== 'Event')
    }
    setEntries(filtered)
  }, [allEntries, showAllChurches, showOnlyEvents, userChurches])

  // Update form church when user churches are loaded
  useEffect(() => {
    if (userChurches.length > 0 && !formData.church) {
      setFormData(prev => ({ ...prev, church: userChurches[0] }))
    }
  }, [userChurches, formData.church])

  const isViewOnly = user?.role === 'viewer'

  // Check if user can edit an entry (events older than 3 years are locked)
  const canEdit = (entry) => {
    if (isViewOnly) return false
    if (isPastEventLocked(entry.event_date)) return false
    return userChurches.includes(entry.church) || user?.role === 'sysadmin' || user?.role === 'admin'
  }

  const resetForm = () => {
    leaveFormAndMaybeReturn()
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    const nextValue = name === 'title' ? truncateChars(value, TITLE_MAX_CHARS) : value
    if (name === 'type' && nextValue !== 'SundaySchool') {
      setSundaySchoolPaused(false)
    }
    setFormData(prev => {
      const next = { ...prev, [name]: nextValue }
      if (name === 'event_date' && /^\d{8}$/.test(nextValue) && (prev.type === 'Event' || next.type === 'Event')) {
        next.post_until = addDaysYYYYMMDD(nextValue, POST_UNTIL_DEFAULT_DAYS)
      }
      if (name === 'type' && nextValue === 'Event') {
        if (/^\d{8}$/.test(prev.event_date) && !prev.post_until) {
          next.post_until = addDaysYYYYMMDD(prev.event_date, POST_UNTIL_DEFAULT_DAYS)
        }
        const range = parseTimeRange(prev.time || prev.time_start || '')
        next.time_start = prev.time_start || range.start
        next.time_end = prev.time_end || range.end
        next.time = combineTimeRange(next.time_start, next.time_end) || next.time_start || ''
      }
      if (name === 'type' && nextValue !== 'Event') {
        next.time = prev.time_start || parseTimeRange(prev.time).start || prev.time || ''
        next.time_start = ''
        next.time_end = ''
      }
      if (name === 'time_start' || name === 'time_end') {
        const start = name === 'time_start' ? nextValue : prev.time_start
        const end = name === 'time_end' ? nextValue : prev.time_end
        next.time = combineTimeRange(start, end) || normalizeTime(start) || ''
      }
      if (name === 'post_to_main') {
        next.post_to_main = nextValue === 'true' || nextValue === true
      }
      return next
    })
  }

  const handleSundaySchoolPausedChange = (checked) => {
    setSundaySchoolPaused(checked)
    if (checked) {
      setFormData(prev => ({
        ...prev,
        title: SUNDAY_SCHOOL_PAUSED_TITLE,
        speaker: '',
        scripture: ''
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        title: prev.title === SUNDAY_SCHOOL_PAUSED_TITLE ? '' : prev.title
      }))
    }
  }

  const isPostUntilExpired = formData.type === 'Event'
    && !!formData.post_until
    && /^\d{8}$/.test(formData.post_until)
    && formData.post_until < getHongKongTodayStr()

  /** Manual stop or expired 刊登至 → event is not shown on church main page */
  const isPublishingStopped = formData.type === 'Event'
    && (formData.post_to_main === false || isPostUntilExpired)

  const handleStopPublishingChange = (checked) => {
    if (checked) {
      setFormData(prev => {
        postUntilBeforeStopRef.current = prev.post_until || ''
        return {
          ...prev,
          post_until: addDaysYYYYMMDD(getHongKongTodayStr(), -1),
          post_to_main: false
        }
      })
    } else {
      setFormData(prev => {
        const restored = postUntilBeforeStopRef.current
          && /^\d{8}$/.test(postUntilBeforeStopRef.current)
          && postUntilBeforeStopRef.current >= getHongKongTodayStr()
          ? postUntilBeforeStopRef.current
          : (prev.event_date
            ? addDaysYYYYMMDD(prev.event_date, POST_UNTIL_DEFAULT_DAYS)
            : '')
        postUntilBeforeStopRef.current = ''
        return {
          ...prev,
          post_until: restored,
          post_to_main: true
        }
      })
    }
  }

  const isEntryNotShown = (entry) => {
    if (entry.type !== 'Event') return false
    if (entry.post_to_main === false) return true
    const until = entry.post_until && /^\d{8}$/.test(entry.post_until)
      ? entry.post_until
      : (entry.event_date ? addDaysYYYYMMDD(entry.event_date, POST_UNTIL_DEFAULT_DAYS) : '')
    return Boolean(until && until < getHongKongTodayStr())
  }

  const setEventDate = (date) => {
    setFormData(prev => ({
      ...prev,
      event_date: date,
      ...(prev.type === 'Event'
        ? { post_until: addDaysYYYYMMDD(date, POST_UNTIL_DEFAULT_DAYS) }
        : {})
    }))
  }

  const suggestedVideoUrl = useMemo(() => {
    if (formData.type !== 'Event') return ''
    return buildEventVideoSuggestion({
      event_date: formData.event_date,
      church: formData.church,
      title: formData.title,
      speaker: formData.speaker,
      time_start: formData.time_start,
      time_end: formData.time_end
    })
  }, [
    formData.type,
    formData.event_date,
    formData.church,
    formData.title,
    formData.speaker,
    formData.time_start,
    formData.time_end
  ])

  const acceptSuggestedVideoUrl = () => {
    if (!suggestedVideoUrl) return
    setFormData(prev => ({ ...prev, video_url: suggestedVideoUrl }))
  }

  const handlePosterChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    if (!formData.church) {
      setError(lang === 'zh' ? '請先選擇堂會再上傳 Poster' : 'Please select church before uploading poster')
      return
    }
    if (!formData.event_date || !/^\d{8}$/.test(formData.event_date)) {
      setError(lang === 'zh' ? '請先填寫活動日期再上傳 Poster' : 'Please enter event date before uploading poster')
      return
    }

    const ext = file.name.split('.').pop().toLowerCase()
    if (!ALLOWED_POSTER_TYPES.includes(ext)) {
      setError(lang === 'zh' ? 'Poster 僅支援 jpg / png / gif / webp / bmp / tiff' : 'Poster must be jpg, png, gif, webp, bmp, or tiff')
      return
    }
    if (file.size > MAX_POSTER_SIZE) {
      setError(lang === 'zh' ? 'Poster 不可超過 10MB' : 'Poster must be under 10MB')
      return
    }

    try {
      setUploadingPoster(true)
      setError('')
      const result = await filesApi.uploadPosterWithPresignedUrl(file, {
        church: formData.church,
        eventDate: formData.event_date,
        entryId: editingEntry?.entry_id
      })
      setFormData(prev => ({ ...prev, poster_key: result.posterKey }))
      if (result.ready) {
        try {
          const { data: { data } } = await filesApi.getPosterDownloadUrl(result.posterKey)
          setPosterPreviewUrl(data.downloadUrl)
        } catch {
          setPosterPreviewUrl('')
        }
        setSuccess(t('posterReady', lang))
      } else {
        setPosterPreviewUrl('')
        setSuccess(lang === 'zh'
          ? 'Poster 已上傳，壓縮可能仍在進行（儲存後仍會保留 key）'
          : 'Poster uploaded; compression may still be running (key is saved)')
      }
    } catch (err) {
      console.error('Poster upload failed:', err)
      setError(lang === 'zh' ? `Poster 上傳失敗：${err.message}` : `Poster upload failed: ${err.message}`)
    } finally {
      setUploadingPoster(false)
    }
  }

  const removePoster = () => {
    setFormData(prev => ({ ...prev, poster_key: '' }))
    setPosterPreviewUrl('')
  }

  const loadPosterPreview = async (posterKey) => {
    if (!posterKey) {
      setPosterPreviewUrl('')
      return
    }
    try {
      const { data: { data } } = await filesApi.getPosterDownloadUrl(posterKey)
      setPosterPreviewUrl(data.downloadUrl)
    } catch {
      setPosterPreviewUrl('')
    }
  }

  const handleTimeBlur = () => {
    if (formData.type === 'Event') {
      const start = formData.time_start?.trim() ? normalizeTime(formData.time_start) : ''
      const end = formData.time_end?.trim() ? normalizeTime(formData.time_end) : ''
      setFormData(prev => ({
        ...prev,
        time_start: start || prev.time_start,
        time_end: end || prev.time_end,
        time: combineTimeRange(start || prev.time_start, end || prev.time_end) || start || ''
      }))
      return
    }
    if (!formData.time?.trim()) return
    const normalized = normalizeTime(formData.time)
    if (normalized) {
      setFormData(prev => ({ ...prev, time: normalized }))
    }
  }

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files)
    const validFiles = []

    if (!formData.church) {
      setError(lang === 'zh' ? '請先選擇堂會再上傳附件' : 'Please select church before uploading files')
      e.target.value = ''
      return
    }
    if (!formData.event_date || !/^\d{8}$/.test(formData.event_date)) {
      setError(lang === 'zh' ? '請先填寫活動日期 (YYYYMMDD) 再上傳附件' : 'Please enter event date (YYYYMMDD) before uploading files')
      e.target.value = ''
      return
    }

    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase()
      if (!ALLOWED_FILE_TYPES.includes(ext)) {
        setError(t('fileTypeError', lang))
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(t('fileTooLarge', lang))
        continue
      }
      validFiles.push(file)
    }

    if (validFiles.length > 0) {
      try {
        setUploadingFiles(true)
        setError('')
        const uploadedFiles = []
        for (const file of validFiles) {
          const result = await filesApi.uploadWithPresignedUrl(file, {
            church: formData.church,
            eventDate: formData.event_date,
            speaker: formData.speaker
          })
          uploadedFiles.push({ filename: result.filename, key: result.key, size: result.size })
        }
        setFormData(prev => ({ ...prev, files: [...prev.files, ...uploadedFiles] }))
        setSuccess(lang === 'zh' ? '附件已上傳' : 'Files uploaded')
      } catch (err) {
        console.error('File upload failed:', err)
        setError(lang === 'zh' ? `附件上傳失敗：${err.message}` : `File upload failed: ${err.message}`)
      } finally {
        setUploadingFiles(false)
        e.target.value = ''
      }
    }
  }

  const removeFile = (index) => {
    setFormData(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }))
  }

  const handleFormFileDownload = async (file) => {
    try {
      await openFileDownload(file, formData.church)
    } catch (err) {
      console.error('Download failed:', err)
      const is404 = err.response?.status === 404 || err.code === 'NOT_FOUND'
      setError(
        is404
          ? (lang === 'zh' ? '附件不存在於伺服器，請重新上傳' : 'Attachment not found, please re-upload')
          : (lang === 'zh' ? '下載附件失敗' : 'Failed to download attachment')
      )
    }
  }

  const renderAttachmentCell = (entry) => {
    const fileList = entry?.file_urls || []
    const fileCount = fileList.length
    if (fileCount === 0) return '-'

    const files = fileList.map(f => resolveFileRef(f, entry.church)).filter(Boolean)
    const menuKey = entry.entry_id
    const isMenuOpen = openAttachments === menuKey

    const handleAttachmentClick = async (e) => {
      e.stopPropagation()
      if (fileCount === 1) {
        try {
          await openFileDownload(files[0], entry.church)
        } catch (err) {
          console.error('Download failed:', err)
          const is404 = err.response?.status === 404 || err.code === 'NOT_FOUND'
          setError(
            is404
              ? (lang === 'zh' ? '附件不存在於伺服器，請重新上傳' : 'Attachment not found, please re-upload')
              : (lang === 'zh' ? '下載附件失敗' : 'Failed to download attachment')
          )
        }
        return
      }
      setOpenAttachments(isMenuOpen ? null : menuKey)
    }

    const handleMenuDownload = async (e, file) => {
      e.preventDefault()
      e.stopPropagation()
      try {
        await openFileDownload(file, entry.church)
      } catch (err) {
        console.error('Download failed:', err)
        const is404 = err.response?.status === 404 || err.code === 'NOT_FOUND'
        setError(
          is404
            ? (lang === 'zh' ? '附件不存在於伺服器，請重新上傳' : 'Attachment not found, please re-upload')
            : (lang === 'zh' ? '下載附件失敗' : 'Failed to download attachment')
        )
      }
    }

    return (
      <div className="file-indicator-wrap">
        <button
          type="button"
          className="file-indicator"
          title={
            lang === 'zh'
              ? `點擊打開附件 (${fileCount})`
              : `Click to open (${fileCount})`
          }
          onClick={handleAttachmentClick}
        >
          📎{fileCount > 1 ? fileCount : ''}
        </button>
        {isMenuOpen && fileCount > 1 && (
          <div className="attachment-menu" role="menu" onClick={e => e.stopPropagation()}>
            {files.map((file, i) => (
              <button
                key={i}
                type="button"
                className="attachment-menu-item"
                role="menuitem"
                title={file.filename || file.key}
                onClick={e => handleMenuDownload(e, file)}
              >
                {file.filename || file.key?.split('/').pop() || `File ${i + 1}`}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  const handleSubmit = async (status = 'saved') => {
    setError('')
    setSuccess('')

    const isPausedSundaySchool = formData.type === 'SundaySchool' && sundaySchoolPaused
    const submitTitle = isPausedSundaySchool
      ? SUNDAY_SCHOOL_PAUSED_TITLE
      : (formData.title || '').trim()

    if (!submitTitle) {
      setError(t('titleRequired', lang))
      return
    }
    if (charCount(submitTitle) > TITLE_MAX_CHARS) {
      setError(t('titleTooLong', lang))
      return
    }
    if (!formData.event_date || !/^\d{8}$/.test(formData.event_date)) {
      setError(t('dateInvalid', lang))
      return
    }
    if (isPastEventLocked(formData.event_date)) {
      setError(lang === 'zh' ? '超過三年的過去活動不可建立或修改' : 'Events older than 3 years cannot be created or modified')
      return
    }
    if (editingEntry && isPastEventLocked(editingEntry.event_date)) {
      setError(lang === 'zh' ? '超過三年的活動已鎖定，無法修改' : 'Events older than 3 years are locked and cannot be modified')
      return
    }
    if (formData.description && formData.description.length > 255) {
      setError(t('descriptionTooLong', lang))
      return
    }
    let normalizedTime = null
    if (formData.type === 'Event') {
      const startRaw = formData.time_start?.trim()
      const endRaw = formData.time_end?.trim()
      if (startRaw && !normalizeTime(startRaw)) {
        setError(t('timeInvalid', lang))
        return
      }
      if (endRaw && !normalizeTime(endRaw)) {
        setError(t('timeInvalid', lang))
        return
      }
      if (endRaw && !startRaw) {
        setError(lang === 'zh' ? '請先填寫開始時間' : 'Please enter start time first')
        return
      }
      normalizedTime = combineTimeRange(formData.time_start, formData.time_end)
    } else {
      normalizedTime = formData.time?.trim() ? normalizeTime(formData.time) : null
      if (formData.time?.trim() && !normalizedTime) {
        setError(t('timeInvalid', lang))
        return
      }
    }
    if (formData.files.some(f => f.filename && !f.key)) {
      setError(lang === 'zh' ? '附件未完整上傳，請重新選擇檔案' : 'Attachment upload incomplete, please re-select files')
      return
    }

    try {
      const submitData = {
        church: formData.church,
        type: formData.type,
        title: submitTitle,
        event_date: formData.event_date,
        time: normalizedTime,
        speaker: isPausedSundaySchool ? null : (formData.speaker || null),
        scripture: isPausedSundaySchool ? null : (formData.scripture || null),
        description: formData.description || null,
        status,
        file_urls: formData.files.map(f => ({ filename: f.filename, key: f.key, size: f.size }))
      }

      if (formData.type === 'Event') {
        submitData.post_until = formData.post_until || addDaysYYYYMMDD(formData.event_date, POST_UNTIL_DEFAULT_DAYS)
        if (submitData.post_until && !/^\d{8}$/.test(submitData.post_until)) {
          setError(lang === 'zh' ? '刊登至日期必須為 YYYYMMDD' : 'Post-until must be YYYYMMDD')
          return
        }
        // Expired / stopped posting → never show on church main page
        const postingEnded = /^\d{8}$/.test(submitData.post_until)
          && submitData.post_until < getHongKongTodayStr()
        submitData.post_to_main = postingEnded ? false : (formData.post_to_main !== false)
        submitData.poster_key = formData.poster_key || null
        submitData.video_url = formData.video_url?.trim() || null
      }

      if (editingEntry) {
        await entriesApi.update(editingEntry.entry_id, submitData)
        setSuccess(status === 'submitted' ? t('entrySubmitted', lang) : t('entrySaved', lang))
      } else {
        await entriesApi.create(submitData)
        setSuccess(status === 'submitted' ? t('entrySubmitted', lang) : t('entrySaved', lang))
      }

      if (editingEntry) {
        await entriesApi.unlock(editingEntry.entry_id)
      }
      loadEntries()
      resetForm()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save entry')
    }
  }

  const handleEdit = async (entry) => {
    if (!canEdit(entry)) {
      setError(lang === 'zh' ? '您沒有權限編輯此條目' : 'You do not have permission to edit this entry')
      return
    }
    try {
      await entriesApi.lock(entry.entry_id)
      setEditingEntry(entry)
      const range = entry.type === 'Event' ? parseTimeRange(entry.time || '') : { start: '', end: '' }
      const paused = entry.type === 'SundaySchool' && entry.title === SUNDAY_SCHOOL_PAUSED_TITLE
      setSundaySchoolPaused(paused)
      setFormData({
        church: entry.church,
        type: entry.type,
        title: entry.title,
        event_date: entry.event_date,
        time: entry.time || '',
        time_start: range.start,
        time_end: range.end,
        speaker: entry.speaker || '',
        scripture: entry.scripture || '',
        description: entry.description || '',
        files: entry.file_urls || [],
        status: entry.status,
        post_to_main: entry.post_to_main !== false,
        poster_key: entry.poster_key || '',
        post_until: entry.post_until || (entry.type === 'Event' ? addDaysYYYYMMDD(entry.event_date, POST_UNTIL_DEFAULT_DAYS) : ''),
        video_url: entry.video_url || ''
      })
      setShowForm(true)
      setLockWarning(null)
      if (entry.poster_key) {
        loadPosterPreview(entry.poster_key)
      } else {
        setPosterPreviewUrl('')
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setLockWarning({ entryId: entry.entry_id, lockedBy: err.response.data.lockedBy })
      } else {
        setError('Failed to lock entry for editing')
      }
    }
  }

  const handleTakeOver = async (entryId) => {
    try {
      await entriesApi.lock(entryId, true)
      setLockWarning(null)
      const entry = entries.find(e => e.entry_id === entryId)
      if (entry) handleEdit(entry)
    } catch (err) {
      setError('Failed to take over entry')
    }
  }

  const handleDelete = async () => {
    if (!editingEntry) return

    if (!window.confirm(lang === 'zh' ? '確定要刪除此條目嗎？' : 'Are you sure you want to delete this entry?')) {
      return
    }

    try {
      await entriesApi.delete(editingEntry.entry_id)
      setSuccess(lang === 'zh' ? '條目已刪除' : 'Entry deleted')
      loadEntries()
      resetForm()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete entry')
    }
  }

  // Date formatting
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return dateStr
    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`
  }

  const getDayOfWeek = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return null
    const year = parseInt(dateStr.substring(0, 4))
    const month = parseInt(dateStr.substring(4, 6)) - 1
    const day = parseInt(dateStr.substring(6, 8))
    return new Date(year, month, day).getDay()
  }

  const getDayLabel = (dateStr) => {
    const day = getDayOfWeek(dateStr)
    const days = {
      en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      zh: ['日', '一', '二', '三', '四', '五', '六']
    }
    return { day, label: days[lang][day] }
  }

  const isPast = (dateStr) => isPastEventDate(dateStr)

  // Sort: date desc → church (KTC, SSC, WIC, STC) → type (Sermon, SundaySchool, Event)
  const sortedEntries = useMemo(() => {
    const churchOrder = { KTC: 0, SSC: 1, WIC: 2, STC: 3 }
    const typeOrder = { Sermon: 0, SundaySchool: 1, Event: 2 }

    return [...entries].sort((a, b) => {
      const dateCompare = b.event_date.localeCompare(a.event_date)
      if (dateCompare !== 0) return dateCompare

      const churchCompare = (churchOrder[a.church] ?? 4) - (churchOrder[b.church] ?? 4)
      if (churchCompare !== 0) return churchCompare

      return (typeOrder[a.type] ?? 3) - (typeOrder[b.type] ?? 3)
    })
  }, [entries])

  if (loading) {
    return <div className="container">{t('loading', lang)}</div>
  }

  return (
    <div className="user-page">
      <section className="page-band">
        <div className="page-band-copy">
          <p className="page-kicker">{lang === 'zh' ? '活動面板' : 'Event desk'}</p>
          <h2 className="page-title">
            {t('welcome', lang)}，{user?.username}
          </h2>
          {userChurches.length > 0 && (
            <div className="church-chips">
              {userChurches.map(c => (
                <span key={c} className="church-chip">{t(c, lang)}</span>
              ))}
            </div>
          )}
        </div>
        <div className="page-band-actions">
          <div className="entry-count">
            <strong>{sortedEntries.length}</strong>
            <span>{lang === 'zh' ? '條目' : 'entries'}</span>
          </div>
          {!isViewOnly && (
            <button className="btn-primary btn-new-entry" onClick={() => setShowForm(true)}>
              + {t('newEntry', lang)}
            </button>
          )}
        </div>
      </section>

      {isViewOnly && (
        <div className="message message-info">
          {lang === 'zh' ? '您目前為唯讀帳戶，只能查看資料，不能新增或修改。' : 'You have a view-only account. You can browse entries but cannot create or edit.'}
        </div>
      )}

      <div className="view-toggle" role="group" aria-label={lang === 'zh' ? '檢視篩選' : 'View filters'}>
        <button
          type="button"
          className={`filter-chip${showAllChurches ? ' active' : ''}`}
          onClick={() => setShowAllChurches(v => !v)}
        >
          {lang === 'zh' ? '所有堂會' : 'All churches'}
        </button>
        <button
          type="button"
          className={`filter-chip${showOnlyEvents ? ' active' : ''}`}
          onClick={() => setShowOnlyEvents(v => !v)}
        >
          {lang === 'zh' ? '只顯示活動' : 'Events only'}
        </button>
        {showAllChurches && (
          <span className="read-only-hint">
            {lang === 'zh' ? '其他堂會為唯讀' : 'Other churches are read-only'}
          </span>
        )}
      </div>

      {/* Messages */}
      {error && <div className="message message-error">{error}</div>}
      {success && <div className="message message-success">{success}</div>}

      {/* Lock Warning */}
      {lockWarning && (
        <div className="message message-warning">
          {t('lockWarning', lang)} {lockWarning.lockedBy}
          <button className="btn-warning" onClick={() => handleTakeOver(lockWarning.entryId)}>
            {t('takeOver', lang)}
          </button>
        </div>
      )}

      {/* Entry Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingEntry ? t('editEntry', lang) : t('newEntry', lang)}</h3>
              <button className="btn-close" onClick={resetForm}>×</button>
            </div>

            <div className="modal-body">
              {/* Error/Success messages inside modal */}
              {error && <div className="message message-error" style={{ marginBottom: '16px' }}>{error}</div>}
              {success && <div className="message message-success" style={{ marginBottom: '16px' }}>{success}</div>}

              <div className="form-grid">
                {/* Church */}
                <div className="form-group">
                  <label>{t('church', lang)} *</label>
                  <select name="church" value={formData.church} onChange={handleInputChange}>
                    {userChurches.map(c => (
                      <option key={c} value={c}>{t(c, lang)}</option>
                    ))}
                  </select>
                </div>

                {/* Type */}
                <div className="form-group">
                  <label>{t('type', lang)} *</label>
                  <select name="type" value={formData.type} onChange={handleInputChange}>
                    <option value="Sermon">{t('sermon', lang)}</option>
                    <option value="SundaySchool">{t('sundaySchool', lang)}</option>
                    <option value="Event">{t('event', lang)}</option>
                  </select>
                </div>

                {/* Title — Sunday School can mark as 暫停一次 */}
                {formData.type === 'SundaySchool' && (
                  <div className="form-group full-width">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={sundaySchoolPaused}
                        onChange={e => handleSundaySchoolPausedChange(e.target.checked)}
                      />
                      {lang === 'zh' ? '暫停一次' : 'Paused once'}
                    </label>
                  </div>
                )}

                {!sundaySchoolPaused && (
                  <div className="form-group full-width">
                    <label>
                      {t('title', lang)} * ({lang === 'zh' ? '不要超過10個字' : 'keep under 10 characters'})
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      maxLength={TITLE_MAX_CHARS}
                    />
                    <span className="label-hint">
                      {charCount(formData.title)}/{TITLE_MAX_CHARS}
                    </span>
                  </div>
                )}

                {/* Event Date */}
                <div className="form-group full-width">
                  <label>
                    {t('eventDate', lang)} *
                    <span className="label-hint"> (YYYYMMDD)</span>
                  </label>
                  <input
                    type="text"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleInputChange}
                    maxLength={8}
                    placeholder="YYYYMMDD"
                  />
                  <div className="sunday-quick-row">
                    <span className="sunday-quick-hint">
                      {lang === 'zh' ? '點擊填入：' : 'Click to fill:'}
                    </span>
                    <div className="sunday-quick-picks">
                      <button
                        type="button"
                        className={`sunday-quick-btn${formData.event_date === sundayOptions.nextSunday ? ' active' : ''}`}
                        title={lang === 'zh' ? '點擊自動填入日期' : 'Click to auto-fill date'}
                        onClick={() => setEventDate(sundayOptions.nextSunday)}
                      >
                        {lang === 'zh' ? '下週日' : 'Next'} {formatDateDisplay(sundayOptions.nextSunday, lang).replace(/^\d{4}-/, '')}
                      </button>
                      <button
                        type="button"
                        className={`sunday-quick-btn${formData.event_date === sundayOptions.nextNextSunday ? ' active' : ''}`}
                        title={lang === 'zh' ? '點擊自動填入日期' : 'Click to auto-fill date'}
                        onClick={() => setEventDate(sundayOptions.nextNextSunday)}
                      >
                        {lang === 'zh' ? '下下週日' : '+2'} {formatDateDisplay(sundayOptions.nextNextSunday, lang).replace(/^\d{4}-/, '')}
                      </button>
                      <button
                        type="button"
                        className={`sunday-quick-btn${formData.event_date === sundayOptions.nextNextNextSunday ? ' active' : ''}`}
                        title={lang === 'zh' ? '點擊自動填入日期' : 'Click to auto-fill date'}
                        onClick={() => setEventDate(sundayOptions.nextNextNextSunday)}
                      >
                        {lang === 'zh' ? '下下下週日' : '+3'} {formatDateDisplay(sundayOptions.nextNextNextSunday, lang).replace(/^\d{4}-/, '')}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Time — Event: start + end; others: single */}
                {formData.type === 'Event' ? (
                  <div className="form-group full-width">
                    <label>{t('time', lang)} ({t('optional', lang)})</label>
                    <p className="upload-hint">
                      {lang === 'zh'
                        ? '請分開填寫開始與結束時間（例如 14:30 與 16:00），不要寫在同一格'
                        : 'Enter start and end separately (e.g. 14:30 and 16:00), not in one box'}
                    </p>
                    <div className="event-time-row">
                      <div className="event-time-field">
                        <label className="sub-label">{t('timeStart', lang)} HH:MM</label>
                        <input
                          type="text"
                          name="time_start"
                          value={formData.time_start}
                          onChange={handleInputChange}
                          onBlur={handleTimeBlur}
                          maxLength={5}
                          placeholder="14:30"
                          inputMode="numeric"
                          autoComplete="off"
                        />
                        <div className="sunday-quick-picks">
                          {['09:30', '10:45', '14:30'].map((timeValue) => (
                            <button
                              key={timeValue}
                              type="button"
                              className={`sunday-quick-btn${formData.time_start === timeValue ? ' active' : ''}`}
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                time_start: timeValue,
                                time: combineTimeRange(timeValue, prev.time_end) || timeValue
                              }))}
                            >
                              {timeValue.replace(/^0/, '')}
                            </button>
                          ))}
                        </div>
                      </div>
                      <span className="event-time-sep" aria-hidden="true">–</span>
                      <div className="event-time-field">
                        <label className="sub-label">{t('timeEnd', lang)} HH:MM</label>
                        <input
                          type="text"
                          name="time_end"
                          value={formData.time_end}
                          onChange={handleInputChange}
                          onBlur={handleTimeBlur}
                          maxLength={5}
                          placeholder="16:00"
                          inputMode="numeric"
                          autoComplete="off"
                        />
                        <div className="sunday-quick-picks">
                          {['12:00', '16:00', '17:00'].map((timeValue) => (
                            <button
                              key={timeValue}
                              type="button"
                              className={`sunday-quick-btn${formData.time_end === timeValue ? ' active' : ''}`}
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                time_end: timeValue,
                                time: combineTimeRange(prev.time_start, timeValue) || prev.time_start || ''
                              }))}
                            >
                              {timeValue.replace(/^0/, '')}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="form-group">
                    <label>{t('time', lang)} HH:MM ({t('optional', lang)})</label>
                    <input
                      type="text"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      onBlur={handleTimeBlur}
                      maxLength={5}
                      placeholder="HH:MM"
                    />
                    <div className="sunday-quick-row">
                      <span className="sunday-quick-hint">
                        {lang === 'zh' ? '點擊填入：' : 'Click to fill:'}
                      </span>
                      <div className="sunday-quick-picks">
                        {['09:30', '10:45'].map((timeValue) => (
                          <button
                            key={timeValue}
                            type="button"
                            className={`sunday-quick-btn${formData.time === timeValue ? ' active' : ''}`}
                            title={lang === 'zh' ? '點擊自動填入時間' : 'Click to auto-fill time'}
                            onClick={() => setFormData(prev => ({ ...prev, time: timeValue }))}
                          >
                            {timeValue.replace(/^0/, '')}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Speaker / Scripture — hidden when Sunday School is paused */}
                {!sundaySchoolPaused && (
                  <>
                    <div className="form-group">
                      <label>{t('speaker', lang)} ({t('optional', lang)})</label>
                      <input type="text" name="speaker" value={formData.speaker} onChange={handleInputChange} />
                    </div>

                    {formData.type !== 'Event' && (
                      <div className="form-group">
                        <label>{t('scripture', lang)}</label>
                        <input type="text" name="scripture" value={formData.scripture} onChange={handleInputChange} />
                      </div>
                    )}
                  </>
                )}

                {/* Event-only fields */}
                {formData.type === 'Event' && (
                  <>
                    <div className="form-group">
                      <label>
                        {t('postUntil', lang)} *
                        <span className="label-hint"> (YYYYMMDD)</span>
                      </label>
                      <input
                        type="text"
                        name="post_until"
                        value={formData.post_until}
                        onChange={handleInputChange}
                        maxLength={8}
                        placeholder="YYYYMMDD"
                        disabled={isPublishingStopped}
                      />
                      <span className="label-hint">
                        {lang === 'zh' ? '預設為活動日期 + 100 天' : 'Default: event date + 100 days'}
                      </span>
                    </div>

                    <div className="form-group full-width stop-publish-box">
                      <label className="checkbox-label stop-publish-label">
                        <input
                          type="checkbox"
                          checked={isPublishingStopped}
                          onChange={e => handleStopPublishingChange(e.target.checked)}
                        />
                        <span>{lang === 'zh' ? '不顯示此活動（停止刊登）' : 'Do not show this event (stop publishing)'}</span>
                      </label>
                      <p className={isPublishingStopped ? 'not-shown-hint' : 'label-hint'}>
                        {isPublishingStopped
                          ? (lang === 'zh'
                            ? '此活動不顯示於堂會主頁。請按「儲存」或「提交」後生效。'
                            : 'This event is not shown on the church main page. Save or Submit to apply.')
                          : (lang === 'zh'
                            ? '勾選後，此活動不會顯示於堂會主頁。'
                            : 'When checked, this event will not be shown on the church main page.')}
                      </p>
                    </div>

                    <div className="form-group full-width">
                      <label>{t('eventPoster', lang)} ({t('optional', lang)})</label>
                      <p className="upload-hint">{t('posterHint', lang)}</p>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.tif,.tiff,image/jpeg,image/png,image/gif,image/webp,image/bmp,image/tiff"
                        onChange={handlePosterChange}
                        disabled={uploadingPoster}
                      />
                      {uploadingPoster && (
                        <p className="upload-hint">{t('posterUploading', lang)}</p>
                      )}
                      {formData.poster_key && (
                        <div className="poster-preview-row">
                          {posterPreviewUrl && (
                            <img
                              src={posterPreviewUrl}
                              alt="poster"
                              className="poster-thumb"
                              width={640}
                              height={360}
                            />
                          )}
                          <div className="poster-meta">
                            <code className="poster-key">{formData.poster_key}</code>
                            <button type="button" className="btn-remove" onClick={removePoster}>
                              {t('removePoster', lang)}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="form-group full-width">
                      <label>{t('videoUrl', lang)} ({t('optional', lang)})</label>
                      {suggestedVideoUrl && (
                        <div className="video-suggest-row">
                          <code className="video-suggest-url" title={suggestedVideoUrl}>
                            {suggestedVideoUrl}
                          </code>
                          <button
                            type="button"
                            className="btn-accept-url"
                            onClick={acceptSuggestedVideoUrl}
                          >
                            {t('acceptSuggestedUrl', lang)}
                          </button>
                        </div>
                      )}
                      <input
                        type="url"
                        name="video_url"
                        value={formData.video_url}
                        onChange={handleInputChange}
                        placeholder="https://…"
                      />
                    </div>
                  </>
                )}

                {/* Description */}
                <div className="form-group full-width">
                  <label>{t('description', lang)} ({t('optional', lang)}, {lang === 'zh' ? '限255字' : 'max 255 chars'})</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange} maxLength={255} rows={3} />
                </div>

                {/* File Upload */}
                <div className="form-group full-width">
                  <label>{t('uploadFiles', lang)} ({t('optional', lang)})</label>
                  <input type="file" multiple onChange={handleFileChange} accept=".pdf,.ppt,.pptx,.doc,.docx,.txt,.md" disabled={uploadingFiles} />
                  {uploadingFiles && (
                    <p className="upload-hint">{lang === 'zh' ? '正在上傳附件…' : 'Uploading files…'}</p>
                  )}
                  {formData.files.length > 0 && (
                    <div className="file-list">
                      {formData.files.map((file, index) => (
                        <div key={index} className="file-item">
                          <button
                            type="button"
                            className="file-name-link"
                            title={lang === 'zh' ? '點擊打開附件' : 'Click to open'}
                            onClick={() => handleFormFileDownload(file)}
                          >
                            {file.filename}
                          </button>
                          <button type="button" onClick={() => removeFile(index)}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {editingEntry && !isPastEventLocked(editingEntry.event_date) && (
                <>
                  <button className="btn-danger" onClick={handleDelete}>{t('delete', lang)}</button>
                  <div style={{ flex: 1 }}></div>
                </>
              )}
              <button className="btn-secondary" onClick={resetForm}>{t('cancel', lang)}</button>
              {!isPastEventLocked(formData.event_date) && !(editingEntry && isPastEventLocked(editingEntry.event_date)) && (
                <>
                  <button className="btn-warning" onClick={() => handleSubmit('saved')}>{t('save', lang)}</button>
                  <button className="btn-success" onClick={() => handleSubmit('submitted')}>{t('submit', lang)}</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="entries-panel">
        <div className="entries-panel-head">
          <h3>{lang === 'zh' ? '條目一覽' : 'Entries'}</h3>
          <span className="entries-panel-sub">
            {showOnlyEvents
              ? (lang === 'zh' ? '活動' : 'Events')
              : (lang === 'zh' ? '講道／主日學／活動' : 'Sermon / Sunday School / Event')}
          </span>
        </div>
        <div className="entries-list">
          {sortedEntries.length === 0 ? (
            <div className="empty-state">
              <p>{lang === 'zh' ? '尚未有任何條目' : 'No entries yet'}</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t('eventDate', lang)}</th>
                  <th>{lang === 'zh' ? '時間' : 'Time'}</th>
                  <th>{t('church', lang)}</th>
                  <th>{t('title', lang)}</th>
                  <th>{t('type', lang)}</th>
                  <th>{t('speaker', lang)}</th>
                  <th>{t('status', lang)}</th>
                  <th>{lang === 'zh' ? '附件' : 'Files'}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map((entry, index) => {
                  const dayInfo = getDayLabel(entry.event_date)
                  const isPastEvent = isPast(entry.event_date)
                  const canEditEntry = canEdit(entry)

                  return (
                    <tr
                      key={entry.entry_id}
                      className={isPastEvent ? 'past-event' : ''}
                      style={{ animationDelay: `${Math.min(index, 12) * 35}ms` }}
                    >
                      <td>
                        <span className={`date-cell${isPastEvent ? ' text-muted' : ''}`}>
                          {formatDate(entry.event_date)}
                        </span>
                        {' '}
                        {dayInfo.day === 0 && <span className="day-sun">({t('daySun', lang)})</span>}
                        {dayInfo.day === 6 && <span className="day-sat">({t('daySat', lang)})</span>}
                        {dayInfo.day !== 0 && dayInfo.day !== 6 && !isPastEvent && (
                          <span className="day-other">({dayInfo.label})</span>
                        )}
                      </td>
                      <td className={isPastEvent ? 'text-muted' : ''}>{entry.time || '-'}</td>
                      <td className={isPastEvent ? 'text-muted' : ''}>
                        <span className="church-tag">{t(entry.church, lang)}</span>
                      </td>
                      <td className={`title-cell${isPastEvent ? ' text-muted' : ''}`}>{entry.title}</td>
                      <td className={isPastEvent ? 'text-muted' : ''}>
                        <span className={`type-tag type-${entry.type}`}>
                          {t(entry.type === 'SundaySchool' ? 'sundaySchool' : entry.type.toLowerCase(), lang)}
                        </span>
                      </td>
                      <td className={isPastEvent ? 'text-muted' : ''}>{entry.speaker || '-'}</td>
                      <td>
                        <div className="status-cell">
                          <span className={`badge badge-${entry.status === 'submitted' ? 'success' : 'warning'}`}>
                            {entry.status === 'submitted' ? t('submitted', lang) : t('saved', lang)}
                          </span>
                          {isEntryNotShown(entry) && (
                            <span className="badge badge-not-shown">
                              {lang === 'zh' ? '不顯示' : 'Not shown'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={isPastEvent ? 'text-muted' : ''}>
                        {renderAttachmentCell(entry)}
                      </td>
                      <td>
                        {canEditEntry ? (
                          <button className="btn-secondary btn-sm btn-edit-row" onClick={() => handleEdit(entry)}>
                            {t('edit', lang)}
                          </button>
                        ) : isPastEventLocked(entry.event_date) ? (
                          <span className="text-muted">{lang === 'zh' ? '已鎖定' : 'Locked'}</span>
                        ) : (
                          <span className="text-muted">{lang === 'zh' ? '唯讀' : 'Read-only'}</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <style jsx>{`
        .user-page {
          min-height: 60vh;
          position: relative;
          padding-bottom: 32px;
        }
        .user-page::before {
          content: '';
          position: absolute;
          inset: -24px -16px auto -16px;
          height: 220px;
          background:
            radial-gradient(ellipse 70% 80% at 10% 0%, rgba(193, 43, 32, 0.22), transparent 55%),
            radial-gradient(ellipse 50% 60% at 90% 20%, rgba(193, 43, 32, 0.12), transparent 50%),
            linear-gradient(180deg, rgba(193, 43, 32, 0.08), transparent 70%);
          pointer-events: none;
          z-index: 0;
        }
        .user-page > * { position: relative; z-index: 1; }

        .page-band {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 20px;
          margin-bottom: 20px;
          padding: 22px 24px;
          border-radius: 16px;
          border: 1px solid color-mix(in srgb, var(--accent-primary) 28%, var(--border-color));
          background:
            linear-gradient(135deg, color-mix(in srgb, var(--accent-primary) 14%, var(--bg-card)), var(--bg-card) 55%);
          box-shadow: 0 10px 28px rgba(48, 34, 33, 0.08);
          animation: band-in 0.45s ease-out both;
        }
        .page-kicker {
          margin: 0 0 6px;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--accent-primary);
        }
        .page-title {
          margin: 0;
          font-family: "Noto Serif TC", "Songti TC", serif;
          font-size: clamp(1.55rem, 2.4vw, 2rem);
          font-weight: 700;
          line-height: 1.25;
          letter-spacing: -0.02em;
        }
        .church-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 12px;
        }
        .church-chip {
          padding: 3px 10px;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--accent-primary);
          background: color-mix(in srgb, var(--accent-primary) 12%, transparent);
          border: 1px solid color-mix(in srgb, var(--accent-primary) 30%, transparent);
        }
        .page-band-actions {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-shrink: 0;
        }
        .entry-count {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          line-height: 1.1;
        }
        .entry-count strong {
          font-family: "Noto Serif TC", serif;
          font-size: 1.75rem;
          color: var(--accent-primary);
        }
        .entry-count span {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }
        .btn-new-entry {
          padding: 12px 20px;
          font-weight: 700;
          border-radius: 999px;
          box-shadow: 0 6px 16px color-mix(in srgb, var(--accent-primary) 35%, transparent);
          transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
        }
        .btn-new-entry:hover {
          transform: translateY(-1px);
          filter: brightness(1.05);
          box-shadow: 0 10px 22px color-mix(in srgb, var(--accent-primary) 40%, transparent);
        }

        .view-toggle {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 18px;
          padding: 10px;
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 12px;
        }
        .filter-chip {
          padding: 8px 14px;
          border-radius: 999px;
          border: 1px solid var(--border-color);
          background: var(--bg-tertiary);
          color: var(--text-secondary);
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.15s;
        }
        .filter-chip:hover {
          border-color: color-mix(in srgb, var(--accent-primary) 45%, var(--border-color));
          color: var(--text-primary);
          transform: translateY(-1px);
        }
        .filter-chip.active {
          background: var(--accent-primary);
          border-color: var(--accent-primary);
          color: #fff;
        }
        .read-only-hint { font-size: 0.8rem; color: var(--text-secondary); margin-left: 4px; }

        .entries-panel {
          border: 1px solid var(--border-color);
          border-radius: 16px;
          background: var(--bg-card);
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(48, 34, 33, 0.06);
          animation: panel-in 0.5s ease-out 0.08s both;
        }
        .entries-panel-head {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-color);
          background:
            linear-gradient(90deg, color-mix(in srgb, var(--accent-primary) 10%, var(--bg-card)), var(--bg-card));
        }
        .entries-panel-head h3 {
          margin: 0;
          font-family: "Noto Serif TC", serif;
          font-size: 1.15rem;
        }
        .entries-panel-sub {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }
        .entries-list { overflow-x: auto; }
        .empty-state { text-align: center; padding: 56px 24px; color: var(--text-muted); }

        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 14px 12px; text-align: left; border-bottom: 1px solid var(--border-color); }
        th {
          background: var(--bg-tertiary);
          font-weight: 700;
          font-size: 0.78rem;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--text-secondary);
          white-space: nowrap;
        }
        tbody tr {
          animation: row-in 0.35s ease-out both;
          transition: background 0.15s ease;
        }
        tbody tr:hover {
          background: color-mix(in srgb, var(--accent-primary) 7%, var(--bg-card));
        }
        .date-cell { font-variant-numeric: tabular-nums; font-weight: 600; }
        .title-cell { font-weight: 600; max-width: 220px; }
        .church-tag, .type-tag {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 6px;
          font-size: 0.78rem;
          font-weight: 600;
          background: var(--bg-tertiary);
          white-space: nowrap;
        }
        .type-tag.type-Event {
          background: color-mix(in srgb, var(--accent-primary) 14%, transparent);
          color: var(--accent-primary);
        }
        .type-tag.type-Sermon {
          background: color-mix(in srgb, #2563eb 14%, transparent);
          color: #2563eb;
        }
        .type-tag.type-SundaySchool {
          background: color-mix(in srgb, #16a34a 14%, transparent);
          color: #16a34a;
        }
        html[data-theme="dark"] .type-tag.type-Sermon { color: #93c5fd; }
        html[data-theme="dark"] .type-tag.type-SundaySchool { color: #86efac; }

        .past-event { opacity: 0.55; }
        .text-muted { color: var(--text-muted); }

        .day-sun { color: #ef4444; font-weight: 600; }
        .day-sat { color: #22c55e; font-weight: 600; }
        .day-other { color: var(--text-secondary); }

        .btn-sm { padding: 6px 14px; font-size: 0.875rem; }
        .btn-edit-row {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
          font-weight: 600;
          transition: background 0.15s, color 0.15s, transform 0.15s;
        }
        .btn-edit-row:hover {
          background: var(--accent-primary);
          color: #fff;
          transform: translateY(-1px);
        }

        @keyframes band-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes panel-in {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes row-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .file-indicator-wrap { position: relative; }
        .file-indicator {
          font-size: 0.875rem;
          color: #ea580c;
          line-height: 1;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
        }
        .file-indicator:hover { color: #c2410c; }
        .attachment-menu {
          position: absolute;
          left: 0;
          top: 100%;
          z-index: 20;
          margin-top: 4px;
          min-width: 140px;
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
          padding: 4px 0;
        }
        .attachment-menu-item {
          display: block;
          width: 100%;
          text-align: left;
          padding: 6px 10px;
          font-size: 0.75rem;
          color: var(--text-primary);
          background: none;
          border: none;
          cursor: pointer;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 200px;
        }
        .attachment-menu-item:hover {
          background: rgba(255, 255, 255, 0.08);
          color: #ea580c;
        }

        /* Modal styles */
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          border: 2px solid var(--input-border, var(--border-color));
          background: var(--bg-input);
        }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 24px; border-bottom: 1px solid var(--border-color); }
        .modal-header h3 { margin: 0; }
        .btn-close { background: none; border: none; color: var(--text-secondary); font-size: 24px; cursor: pointer; }
        .modal-body { padding: 24px; }
        .modal-footer { display: flex; justify-content: flex-end; gap: 12px; padding: 16px 24px; border-top: 1px solid var(--border-color); }

        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .form-group { display: flex; flex-direction: column; gap: 8px; }
        .form-group.full-width { grid-column: span 2; }
        .form-group label { font-size: 0.875rem; color: var(--text-secondary); }
        .form-group .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 0.95rem;
          color: var(--text-primary);
        }
        .form-group .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          min-width: 18px;
          flex-shrink: 0;
          accent-color: var(--accent-primary, #c12b20);
          cursor: pointer;
        }
        .stop-publish-box {
          padding: 14px;
          border: 2px solid var(--accent-primary, #c12b20);
          border-radius: var(--border-radius, 8px);
          background: rgba(193, 43, 32, 0.06);
          gap: 8px;
        }
        .stop-publish-label {
          font-weight: 600;
          font-size: 1rem !important;
          color: var(--text-primary, #222) !important;
        }
        .not-shown-hint {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--accent-danger, #c0392b);
        }
        .status-cell {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          align-items: center;
        }
        .badge-not-shown {
          background: #fde8e8;
          color: #b91c1c;
          border: 1px solid #f5c2c2;
          font-size: 0.75rem;
          padding: 2px 8px;
          border-radius: 999px;
        }
        .label-hint { font-size: 0.75rem; color: var(--text-muted); font-weight: normal; }
        .sunday-quick-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: -2px;
          min-width: 0;
        }
        .sunday-quick-hint {
          flex: 0 0 auto;
          margin: 0;
          font-size: 0.62rem;
          color: var(--text-muted);
          line-height: 1.2;
          white-space: nowrap;
        }
        .sunday-quick-picks {
          display: flex;
          flex: 1;
          flex-wrap: nowrap;
          gap: 4px;
          min-width: 0;
        }
        .sunday-quick-btn {
          flex: 1;
          min-width: 0;
          padding: 4px 2px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          color: var(--text-secondary);
          font-size: 0.62rem;
          line-height: 1.2;
          cursor: pointer;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          transition: border-color 0.15s, background-color 0.15s, color 0.15s;
        }
        .sunday-quick-btn:hover {
          border-color: var(--accent-info);
          color: var(--text-primary);
          background: var(--bg-hover);
        }
        .sunday-quick-btn.active {
          border-color: var(--accent-primary);
          color: #fff;
          background: color-mix(in srgb, var(--accent-primary) 85%, #000);
        }
        .form-group.full-width:has(.sunday-quick-row),
        .form-group:has(.sunday-quick-row) { gap: 4px; }

        .file-list { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 8px; }
        .file-item { display: flex; align-items: center; gap: 8px; padding: 4px 12px; background: var(--bg-tertiary); border-radius: 4px; font-size: 0.875rem; }
        .file-name-link {
          background: none;
          border: none;
          padding: 0;
          color: #ea580c;
          cursor: pointer;
          font-size: 0.875rem;
          text-align: left;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .file-name-link:hover { color: #c2410c; }
        .file-item > button:last-child { background: none; border: none; color: var(--accent-danger); cursor: pointer; font-size: 16px; }

        .upload-hint { margin: 4px 0 8px; font-size: 0.8rem; color: var(--text-secondary); }
        .poster-preview-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: flex-start;
          margin-top: 10px;
        }
        .poster-thumb {
          width: 100%;
          max-width: 640px;
          height: auto;
          aspect-ratio: 16 / 9;
          border-radius: 4px;
          border: 1px solid rgba(255,255,255,0.12);
          background: #000;
          object-fit: cover;
        }
        .poster-meta { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
        .poster-key {
          font-size: 0.75rem;
          word-break: break-all;
          color: var(--text-secondary);
        }
        .video-suggest-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
          margin-bottom: 8px;
          padding: 8px 10px;
          background: var(--bg-tertiary, rgba(255,255,255,0.04));
          border-radius: 6px;
        }
        .video-suggest-url {
          flex: 1;
          min-width: 0;
          font-size: 0.75rem;
          word-break: break-all;
          color: var(--text-secondary, #a0aec0);
        }
        .btn-accept-url {
          flex-shrink: 0;
          padding: 4px 12px;
          border: none;
          border-radius: 4px;
          background: var(--accent-primary, #c12b20);
          color: #fff;
          font-size: 0.8rem;
          cursor: pointer;
        }
        .btn-accept-url:hover { filter: brightness(1.1); }
        .event-time-row {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          gap: 10px 14px;
        }
        .event-time-field {
          flex: 1;
          min-width: 140px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .event-time-field .sub-label {
          font-size: 0.8rem;
          color: var(--text-secondary, #a0aec0);
          font-weight: 500;
        }
        .event-time-sep {
          align-self: center;
          margin-top: 22px;
          font-size: 1.25rem;
          color: var(--text-secondary, #a0aec0);
        }
        .event-time-field .sunday-quick-picks {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        @media (max-width: 768px) {
          .page-band {
            flex-direction: column;
            align-items: stretch;
            padding: 18px;
          }
          .page-band-actions {
            justify-content: space-between;
          }
          .form-grid { grid-template-columns: 1fr; }
          .form-group.full-width { grid-column: span 1; }
          .title-cell { max-width: 140px; }
        }
      `}</style>
    </div>
  )
}